import { useEffect, useRef } from "react";
import { customSanitize } from "../../../utils/customSanitize";
import { y_variant_small } from "../../../utils/animation/transitions";
import * as motion from "motion/react-m";
import { Section } from "../Section/Section";
import { ContentTag } from "@/components/ContentTag/ContentTag";

/**
 * Defines the possible HTML tags for the Headline component
 * @typedef {'h1-hero' | 'h1-pages' | 'h1-posts' | 'h2' | 'h3' | 'h2-small'} HeadlineTagType
 */
export type HeadlineTagType =
  | "h1-hero"
  | "h1-pages"
  | "h1-posts"
  | "h2"
  | "h3"
  | "h2-small";

/**
 * Props for the Headline component
 * @typedef {Object} HeadlineProps
 * @property {HeadlineTagType} headline_html_tag - The HTML tag to use for the headline
 * @property {string} headline - The headline text content (can include HTML)
 * @property {string} [extraClass] - Additional CSS classes to apply to the headline container
 * @property {string} [overline] - Optional text to display above the headline (can include HTML)
 * @property {string} [subline] - Optional text to display below the headline (can include HTML)
 * @property {boolean} [syncAnimation=false] - Whether to sync the animation with parent components
 * @property {string} [headerClassName] - Additional CSS classes to apply to all <h?> elements only
 * @property {string} [strongColor] - Color to apply to <strong> elements within the headline
 * @property {boolean} [isQuote=false] - Whether the headline is a quote (applies specific styling)
 * @property {boolean} [wrapInSection=false] - Whether to wrap the headline in a Section component
 * @property {boolean} [disableAnimation=false] - Whether to animate the headline with a motion div
 * @property {string} [textAlignClass] - Alignment CSS class to apply to all text elements
 */
export type HeadlineProps = {
  headline_html_tag: HeadlineTagType;
  headline: string;
  /** extraClass Description about propName. */
  extraClass?: string;
  /** overline Description about propName. */
  overline?: string;
  subline?: string;
  introText?: string;
  syncAnimation?: boolean;
  headerClassName?: string;
  strongColor?: string;
  isQuote?: boolean;
  wrapInSection?: boolean;
  disableAnimation?: boolean;
  textAlignClass?: string;
};

/**
 * Headline component for displaying titles with optional overline and subline text
 *
 * The component supports different HTML tags (h1, h2, h3) with variant styles,
 * animations, and additional customization options.
 *
 * @param {HeadlineProps} props - The component props
 * @returns {JSX.Element} The rendered Headline component
 *
 * @example
 * // Basic usage
 * <Headline headline_html_tag="h1-hero" headline="Main Title" />
 *
 * @example
 * // With overline and subline
 * <Headline
 *   headline_html_tag="h2"
 *   headline="Section Title"
 *   overline="CATEGORY"
 *   subline="Additional information about this section"
 * />
 *
 * @example
 * // With custom styling for strong elements
 * <Headline
 *   headline_html_tag="h1-pages"
 *   headline="Welcome to our <strong>Amazing</strong> Website"
 *   strongColor="#ff0000"
 * />
 */
export const Headline = ({
  headline_html_tag,
  headline,
  extraClass = "",
  overline,
  subline,
  introText,
  syncAnimation = false,
  headerClassName,
  strongColor,
  isQuote = false,
  wrapInSection = false,
  disableAnimation = false,
  textAlignClass = "",
}: HeadlineProps) => {
  /**
   * Reference to the headline element for applying dynamic styles
   * @type {React.RefObject<HTMLHeadingElement>}
   */
  const titleRef = useRef<HTMLHeadingElement>(null);

  /**
   * Apply the strongColor to <strong> elements within the headline
   */
  useEffect(() => {
    if (titleRef.current && strongColor) {
      const pElement = titleRef.current.querySelector("strong");

      if (pElement) {
        // Adding styles directly to the <p> element
        pElement.style.color = strongColor;
      }
    }
  });

  const getHeadline = () => {
    if (!headline) return <></>;

    const titleStyles = subline ? "pb-0" : "";
    /* jscpd:ignore-start */
    switch (headline_html_tag) {
      case "h1-hero":
      case "h1-pages":
      case "h1-posts":
        return (
          <h1
            ref={titleRef}
            className={`${titleStyles} ${headline_html_tag} ${headerClassName || ""} ${textAlignClass}`}
            dangerouslySetInnerHTML={{ __html: customSanitize(headline) }}
          />
        );
      case "h2":
        return (
          <h2
            ref={titleRef}
            className={`${titleStyles} ${isQuote ? "quotes-banner-title" : ""} ${headerClassName || ""} ${textAlignClass}`}
            dangerouslySetInnerHTML={{ __html: customSanitize(headline) }}
          />
        );
      case "h2-small":
        return (
          <h2
            ref={titleRef}
            className={`h2-small ${titleStyles} ${isQuote ? "quotes-banner-title" : ""} ${headerClassName || ""} ${textAlignClass}`}
            dangerouslySetInnerHTML={{ __html: customSanitize(headline) }}
          />
        );
      case "h3":
        return (
          <h3
            ref={titleRef}
            className={`${titleStyles} ${isQuote ? "quotes-banner-title" : ""} ${headerClassName || ""} ${textAlignClass}`}
            dangerouslySetInnerHTML={{ __html: customSanitize(headline) }}
          />
        );
      default:
        return <></>;
    }
  };

  const getOverline = () => {
    if (!overline) return <></>;

    return isQuote ? (
      <blockquote
        className={`overline overline-${headline_html_tag} ${textAlignClass} quotes-banner-subline`}
        dangerouslySetInnerHTML={{ __html: customSanitize(overline) }}
      />
    ) : (
      <p
        className={`overline overline-${headline_html_tag} ${textAlignClass}`}
        dangerouslySetInnerHTML={{ __html: customSanitize(overline) }}
      />
    );
  };

  const getSubline = () => {
    if (!subline) return <></>;

    return isQuote ? (
      <blockquote
        className={`subline subline-${headline_html_tag} ${textAlignClass} quotes-banner-subline`}
        dangerouslySetInnerHTML={{ __html: customSanitize(subline) }}
      />
    ) : (
      <p
        className={`subline subline-${headline_html_tag} ${textAlignClass}`}
        dangerouslySetInnerHTML={{ __html: customSanitize(subline) }}
      />
    );
  };
  /* jscpd:ignore-end */

  if (disableAnimation) {
    return (
      <>
        {getOverline()}
        {getHeadline()}
        {getSubline()}
        {introText && (
          <ContentTag extraClass={textAlignClass} content={introText} />
        )}
      </>
    );
  }

  /**
   * Animation properties based on syncAnimation flag
   * When syncAnimation is false, the headline animates when it enters the viewport
   */
  const animationProps = syncAnimation
    ? {}
    : {
        viewport: { amount: 0.3, once: true },
        initial: "initial",
        whileInView: "animate",
      };

  const inner = (
    <motion.div {...animationProps} className="is-clipped">
      <motion.div
        variants={y_variant_small}
        className={`${extraClass} ${subline ? `${headline_html_tag}` : ""} is-clipped`}
      >
        {getOverline()}
        {getHeadline()}
        {getSubline()}
        {introText && (
          <ContentTag extraClass={textAlignClass} content={introText} />
        )}
      </motion.div>
    </motion.div>
  );

  if (wrapInSection) {
    return (
      <Section tag="div" disableColumns className="py-0">
        {inner}
      </Section>
    );
  }

  return inner;
};
